import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing the gallery in-place.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Prevent morph from updating image src/srcset when the optimistic swap
    // already loaded the correct image. Without this, morph sees a different
    // URL format (e.g. width=1200 vs width=765) and triggers a re-download,
    // briefly showing the blurred placeholder.
    this.#preserveOptimisticImages(newMediaGallery);

    // Remove placeholders from new HTML — they exist for initial page load
    // progressive reveal only. During variant switch the main image is already
    // visible; morph adding a placeholder back causes a blur flash because
    // progressive-image.js already removed/faded the original.
    for (const el of newMediaGallery.querySelectorAll('.image-snippet__placeholder')) {
      el.remove();
    }

    // Prevent morph from stripping Swiper's runtime inline styles (opacity,
    // transforms) and classes. Mark swiper internals so morph skips their
    // attribute updates but still recurses into children (updating image src).
    this.#preserveSwiperState(newMediaGallery);

    morph(this, newMediaGallery);

    // Clean up temporary skip attributes
    for (const el of this.querySelectorAll('[data-skip-node-update]')) {
      el.removeAttribute('data-skip-node-update');
    }

    // Lightweight Swiper refresh (no destroy/reinit) for slide count changes
    this.refs.slideshow?.updateAfterMorph?.();
  };

  /**
   * Copies current image attributes to the new gallery so morph skips them.
   * Compares filenames to detect same-image-different-URL-format cases.
   * @param {Element} newGallery
   */
  #preserveOptimisticImages(newGallery) {
    const pairs = [
      ['.image-snippet__main', '.image-snippet__main'],
    ];

    for (const [oldSel, newSel] of pairs) {
      const currentImg = this.querySelector(oldSel);
      const newImg = newGallery.querySelector(newSel);
      if (!(currentImg instanceof HTMLImageElement) || !newImg) continue;

      const currentFile = this.#extractFilename(currentImg.src);
      const newFile = this.#extractFilename(newImg.getAttribute('src'));

      if (currentFile && newFile && currentFile === newFile) {
        newImg.setAttribute('src', currentImg.getAttribute('src') || currentImg.src);
        if (currentImg.hasAttribute('srcset')) {
          newImg.setAttribute('srcset', currentImg.getAttribute('srcset'));
        } else {
          newImg.removeAttribute('srcset');
        }
      }
    }
  }

  /**
   * Marks Swiper-managed elements with data-skip-node-update so morph
   * preserves their runtime inline styles but still updates their children.
   * @param {Element} newGallery
   */
  #preserveSwiperState(newGallery) {
    const selector = '.swiper-wrapper, .swiper-slide';
    for (const el of this.querySelectorAll(selector)) {
      el.setAttribute('data-skip-node-update', '');
    }
    for (const el of newGallery.querySelectorAll(selector)) {
      el.setAttribute('data-skip-node-update', '');
    }
  }

  /**
   * Extracts filename from a Shopify CDN URL (ignoring query params).
   * @param {string | null} url
   * @returns {string | null}
   */
  #extractFilename(url) {
    if (!url) return null;
    try {
      const pathname = new URL(url, 'https://x.com').pathname;
      return pathname.split('/').pop()?.split('?')[0] || null;
    } catch {
      return null;
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
